import React, { useEffect, useId, useState } from 'react';
import { AppButton, AppCheckbox, AppModal } from '../../components/ui';

const STORAGE_KEY = 'client-annuaire-export-columns';

const EXPORT_FIELDS = [
  { key: 'client', label: 'Client' },
  { key: 'commercial', label: 'Commercial' },
  { key: 'gerant', label: 'Gérant' },
  {
    key: 'contact',
    label: 'Contact',
    children: [
      { key: 'contact_email', label: 'E-mail' },
      { key: 'contact_phone', label: 'Téléphone' },
      { key: 'site_web', label: 'Site web' },
    ],
  },
  { key: 'statut', label: 'Statut' },
  { key: 'iti', label: 'itiConseil' },
  { key: 'kkp', label: 'Koikispass' },
  { key: 'klub', label: 'Klub' },
  { key: 'contrat', label: 'Contrat en cours' },
  { key: 'commande', label: 'Dernière commande' },
  { key: 'dolibarr', label: 'Identifiants' },
];

const LEAF_KEYS = EXPORT_FIELDS.flatMap((field) => (
  field.children ? field.children.map((child) => child.key) : [field.key]
));

const CONTACT_KEYS = ['contact_email', 'contact_phone', 'site_web'];

const orderKeys = (keys) => LEAF_KEYS.filter((key) => keys.includes(key));

const loadSelection = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [...LEAF_KEYS];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...LEAF_KEYS];
    const next = orderKeys(parsed);
    return next.length ? next : [...LEAF_KEYS];
  } catch {
    return [...LEAF_KEYS];
  }
};

const saveSelection = (keys) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(orderKeys(keys)));
};

const ClientAnnuaireExportModal = ({
  show = false,
  exporting = false,
  onClose,
  onConfirm,
}) => {
  const baseId = useId();
  const [selected, setSelected] = useState(loadSelection);

  useEffect(() => {
    if (show) setSelected(loadSelection());
  }, [show]);

  const allSelected = LEAF_KEYS.every((key) => selected.includes(key));
  const contactSelectedCount = CONTACT_KEYS.filter((key) => selected.includes(key)).length;
  const contactOn = contactSelectedCount === CONTACT_KEYS.length;
  const contactIndeterminate = contactSelectedCount > 0 && !contactOn;

  const toggleKey = (key) => {
    setSelected((current) => orderKeys(
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
    ));
  };

  const setContact = (on) => {
    setSelected((current) => orderKeys(
      on
        ? [...current.filter((item) => !CONTACT_KEYS.includes(item)), ...CONTACT_KEYS]
        : current.filter((item) => !CONTACT_KEYS.includes(item)),
    ));
  };

  const handleConfirm = () => {
    const keys = orderKeys(selected);
    if (!keys.length || exporting) return;
    saveSelection(keys);
    onConfirm?.(keys);
  };

  return (
    <AppModal
      show={show}
      title="Colonnes à exporter"
      onClose={onClose}
      closeDisabled={exporting}
      centered
      footer={(
        <>
          <AppButton variant="outline-secondary" onClick={onClose} disabled={exporting}>
            Annuler
          </AppButton>
          <AppButton
            onClick={handleConfirm}
            disabled={selected.length === 0}
            loading={exporting}
            loadingLabel="Export…"
          >
            Exporter
          </AppButton>
        </>
      )}
    >
      <p className="text-secondary-light mb-3">
        Le fichier reprend les filtres et le tri du tableau.
      </p>
      <div className="d-flex gap-3 mb-3">
        <AppButton
          variant="link"
          size="sm"
          className="p-0"
          disabled={exporting || allSelected}
          onClick={() => setSelected([...LEAF_KEYS])}
        >
          Tout sélectionner
        </AppButton>
        <AppButton
          variant="link"
          size="sm"
          className="p-0"
          disabled={exporting || selected.length === 0}
          onClick={() => setSelected([])}
        >
          Tout désélectionner
        </AppButton>
      </div>
      <div className="client-annuaire-export-columns">
        {EXPORT_FIELDS.map((field) => {
          if (field.children) {
            return (
              <div key={field.key}>
                <AppCheckbox
                  id={`${baseId}-contact`}
                  label={field.label}
                  checked={contactOn}
                  indeterminate={contactIndeterminate}
                  disabled={exporting}
                  onChange={() => setContact(!contactOn)}
                />
                <div className="client-annuaire-export-contact">
                  {field.children.map((child) => (
                    <AppCheckbox
                      key={child.key}
                      id={`${baseId}-${child.key}`}
                      label={child.label}
                      checked={selected.includes(child.key)}
                      disabled={exporting}
                      onChange={() => toggleKey(child.key)}
                    />
                  ))}
                </div>
              </div>
            );
          }

          return (
            <AppCheckbox
              key={field.key}
              id={`${baseId}-${field.key}`}
              label={field.label}
              checked={selected.includes(field.key)}
              disabled={exporting}
              onChange={() => toggleKey(field.key)}
            />
          );
        })}
      </div>
    </AppModal>
  );
};

export default ClientAnnuaireExportModal;
